import 'reflect-metadata';
import '@/index.css';

import React, { useEffect, useState } from 'react';
import {
	Outlet,
	Scripts,
	Links,
	Meta,
	useLocation,
	useNavigate,
	ScrollRestoration,
} from 'react-router';
import { Provider } from 'react-redux';
import { skipToken } from '@reduxjs/toolkit/query';
import * as Sentry from '@sentry/react-router';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { v4 as uuidv4 } from 'uuid';

import store, { useAppDispatch } from './store';
import { syncSessionAction } from './store/slices/auth/actions/sync-session.action';
import { useGetProfileQuery } from './store/api/auth.api.js';

import {
	ReferralProvider,
	SignUpProvider,
	MarketsProvider,
	ChatProvider,
	SocketProvider,
	ThirdPartyAuthProvider,
} from './contexts';
import { TooltipProvider } from './components/ui/tooltip';

import PageLayout from './pages/Layout';
import { GlobalErrorFallback } from './components/ui/global-error-fallback';
import { Toaster } from '@/components/ui/toaster';
import AppSmartBanner from '@/components/core/app-smart-banner';
import { ForceAppUpdateGate } from '@/components/core/force-app-update-gate';

import { useIsRegisteredUser, useInitAnalytics, usePreventZoomOut } from './hooks';
import { RedirectGuard } from './routes/redirect-guard';
import { UserRole } from '@sportspredict/shared';
import { GOOGLE_API_CLIENT_ID, ROUTES } from '@/constants';
import { getLegacyRedirectPath, preventIosInputZoom } from '@/utils';
import { PROBABILITY_EVENT_PAGE_NAME } from '@/modules/probability-event/utils/probability-event.utils';
import { COURSES_PAGE_NAME } from '@/modules/courses/constants/courses.constants';
import mixpanelOriginal from './configs/mixpanel';

const mixpanel: any = mixpanelOriginal;

declare global {
	interface Window {
		store: typeof store;
	}
}

if (typeof window !== 'undefined') {
	window.store = store;
}

const SLUG_TO_PAGE_KEY: Record<string, string> = {
	auth: 'Auth',
	login: 'Login',
	'email-verification': 'EmailVerification',
	'code-verification': 'CodeVerification',
	'reset-password': 'ResetPassword',
	'parent-email-verification': 'ParentEmailVerification',
	'parental-consent': 'ParentalConsent',
	'external-leaderboard': 'ExternalLeaderboard',
	'lobby-details': 'LobbyDetails',
	// The bingo mode is a view of a lobby, so it shares the lobby page layout.
	bingo: 'LobbyDetails',
};

function _getCurrentPage(url: string) {
	if (url === '/' || url === '') return 'home';
	if (url.endsWith('/')) url = url.slice(0, -1);
	if (url.startsWith(`${ROUTES.PROBABILITY_EVENT}/`)) return PROBABILITY_EVENT_PAGE_NAME;
	if (url === ROUTES.COURSES || url.startsWith(`${ROUTES.COURSES}/`)) return COURSES_PAGE_NAME;
	// Bingo match pages (/bingo/:matchId) belong to the lobby, like /bingo itself.
	if (url.startsWith(`${ROUTES.BINGO}/`)) return SLUG_TO_PAGE_KEY.bingo;
	const part = url.split('/').pop()?.split('?')[0].toLowerCase() || '';
	if (SLUG_TO_PAGE_KEY[part]) return SLUG_TO_PAGE_KEY[part];
	return part.replace(/-/g, '') || 'home';
}

function AppWrapper({ children }: { children: React.ReactNode }) {
	usePreventZoomOut();
	useInitAnalytics();
	const dispatch = useAppDispatch();
	const location = useLocation();
	const navigate = useNavigate();

	const [sessionSynced, setSessionSynced] = useState(false);
	const isRegisteredUser = useIsRegisteredUser();
	const { data: user, isLoading } = useGetProfileQuery(isRegisteredUser ? undefined : skipToken);

	useEffect(() => {
		const redirectPath = getLegacyRedirectPath(location.pathname);
		if (redirectPath) navigate(`${redirectPath}${location.search}`, { replace: true });
	}, [location.pathname, location.search, navigate]);

	useEffect(() => {
		dispatch(syncSessionAction() as any).finally(() => setSessionSynced(true));
	}, [dispatch]);

	useEffect(() => {
		preventIosInputZoom();
	}, []);

	useEffect(() => {
		const onVis = () => {
			if (document.visibilityState === 'visible') {
				mixpanel.track('[APP OPENED]', {
					userId: user?.id,
					userEmail: user?.email,
					page: location.pathname,
					returnedAt: new Date().toISOString(),
				});
			}
		};
		document.addEventListener('visibilitychange', onVis);
		return () => document.removeEventListener('visibilitychange', onVis);
	}, [user, location.pathname]);

	useEffect(() => {
		if (user?.role === UserRole.Admin) return;
		if (user?.email?.toLowerCase().includes('testloading')) return;
		if (user?.id) {
			mixpanel.identify(user.id);
			mixpanel.people.set({
				$email: user.email,
				$name: user.username,
			});
		}
	}, [user]);

	useEffect(() => {
		if (typeof window === 'undefined') return;
		const DEVICE_ID_KEY = 'deviceId';
		let deviceId = localStorage.getItem(DEVICE_ID_KEY);
		if (!deviceId) {
			deviceId = uuidv4();
			localStorage.setItem(DEVICE_ID_KEY, deviceId);
		}
	}, []);

	const currentPage = _getCurrentPage(location.pathname);

	return (
		<GoogleOAuthProvider clientId={GOOGLE_API_CLIENT_ID}>
			<Sentry.ErrorBoundary fallback={(props) => <GlobalErrorFallback {...props} />}>
				<SocketProvider>
					<ChatProvider>
						<MarketsProvider>
							<ReferralProvider>
								<SignUpProvider>
									<ThirdPartyAuthProvider>
										<RedirectGuard
											isRegisteredUser={isRegisteredUser}
											user={user}
											isLoading={!sessionSynced || isLoading}
										>
											<PageLayout currentPageName={currentPage}>
												<TooltipProvider>
													<AppSmartBanner />
													<ForceAppUpdateGate />
													{children}
													<Toaster />
												</TooltipProvider>
											</PageLayout>
										</RedirectGuard>
									</ThirdPartyAuthProvider>
								</SignUpProvider>
							</ReferralProvider>
						</MarketsProvider>
					</ChatProvider>
				</SocketProvider>
			</Sentry.ErrorBoundary>
		</GoogleOAuthProvider>
	);
}

export function Layout({ children }: { children: React.ReactNode }) {
	return (
		<html lang="en" style={{ minHeight: '100%' }}>
			<head>
				<meta charSet="utf-8" />
				<meta name="viewport" content="width=device-width, initial-scale=1" />
				<title>Sports Predict</title>
				<link rel="icon" type="image/png" sizes="16x16" href="/favicon-16x16.png" />
				<link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32.png" />
				<link rel="icon" type="image/png" sizes="64x64" href="/favicon-64x64.png" />
				<Meta />
				<Links />
				<style
					dangerouslySetInnerHTML={{
						__html: `
          html, body { min-height: 100%; }
          html { background: #101423 url('/images/background.webp') center / cover no-repeat fixed; }
        `,
					}}
				/>
			</head>
			<body>
				{children}
				<ScrollRestoration />
				<Scripts />
				<script src="https://accounts.google.com/gsi/client" async defer />
				{/* Defines window.AppleID for web "Continue with Apple". It lived in the old
				    index.html and was lost when that file was removed; without it the button
				    only shows "Apple login unavailable". */}
				<script
					src="https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js"
					async
					defer
				/>
			</body>
		</html>
	);
}

export default function App() {
	return (
		<Provider store={store}>
			<AppWrapper>
				<Outlet />
			</AppWrapper>
		</Provider>
	);
}
